import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomSelect from '@/components/ui/CustomSelect.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import type { SimulationResponse, SimulationType } from '@/types/simulation.ts';
import { useCatalogOptions, useCatalogLabelMap } from '@/hooks/useCatalog.ts';

interface QuickPracticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  maestroId?: string;
}

export default function QuickPracticeModal({ isOpen, onClose, maestroId }: QuickPracticeModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { options: simulationTypeOptions } = useCatalogOptions('SimulationType');
  const { options: sectorOptions } = useCatalogOptions('Sector');
  const { options: allSubSectorOptions } = useCatalogOptions('SubSector');
  const difficultyLabels = useCatalogLabelMap('Difficulty');

  const [simulationType, setSimulationType] = useState('cold-calling');
  const [objections, setObjections] = useState('');
  const [sector, setSector] = useState('');
  const [subSector, setSubSector] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState(3);

  const subSectorOptions = sector
    ? allSubSectorOptions.filter((o) => o.parentSlug === sector)
    : [];

  const handleCreate = () => {
    const quickSim: SimulationResponse = {
      id: crypto.randomUUID(),
      name: `${t('quickPractice.namePrefix')} ${simulationTypeOptions.find((o) => o.value === simulationType)?.label ?? simulationType}`,
      simulationType: simulationType as SimulationType,
      salePhase: '',
      purpose: 'evaluate',
      kpisToEvaluate: [],
      objections: objections || undefined,
      sector: sector || undefined,
      subSector: subSector || undefined,
      priceRange: undefined,
      difficultyLevel,
      assignedMaestroId: undefined,
      status: 'Draft',
      language: (i18n.resolvedLanguage === 'it' ? 'it' : 'es'),
      assignedSalespersonIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      documentsReadiness: 'NoDocuments',
      totalDocumentsCount: 0,
      processingDocumentsCount: 0,
      readyDocumentsCount: 0,
      failedDocumentsCount: 0,
      rejectedDocumentsCount: 0,
    };

    navigate(`/simulation/${quickSim.id}`, {
      state: { quickPractice: quickSim, ...(maestroId ? { maestroId } : {}) },
    });
  };

  const inputClass =
    'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('quickPractice.title')}</DialogTitle>
          <DialogDescription>{t('quickPractice.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">{t('quickPractice.simulationTypeLabel')}</label>
            <CustomSelect
              value={simulationType}
              onChange={setSimulationType}
              options={simulationTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">{t('quickPractice.objectionsLabel')}</label>
            <textarea
              value={objections}
              onChange={(e) => setObjections(e.target.value)}
              placeholder={t('quickPractice.objectionsPlaceholder')}
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">{t('quickPractice.sectorLabel')}</label>
              <CustomSelect
                value={sector}
                onChange={(val) => { setSector(val); setSubSector(''); }}
                placeholder={t('quickPractice.noSector')}
                options={[
                  { value: '', label: t('quickPractice.noSector') },
                  ...sectorOptions.map((s) => ({ value: s.value, label: s.label })),
                ]}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">{t('quickPractice.subSectorLabel')}</label>
              <CustomSelect
                value={subSector}
                onChange={setSubSector}
                disabled={!sector}
                placeholder={sector ? t('quickPractice.selectSubSector') : '—'}
                options={[
                  { value: '', label: sector ? t('quickPractice.selectSubSector') : '—' },
                  ...subSectorOptions.map((s) => ({ value: s.value, label: s.label })),
                ]}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-muted-foreground">{t('quickPractice.difficultyLabel')}</label>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setDifficultyLevel(level)}
                  className={`flex flex-1 flex-col items-center gap-0.5 rounded-lg border px-1.5 py-2 text-xs transition-colors ${
                    difficultyLevel === level
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  <span className="text-sm font-bold">{level}</span>
                  <span className="text-[9px] leading-tight">{difficultyLabels.get(String(level)) ?? String(level)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate}>{t('quickPractice.createButton')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
